import React, { FC } from 'react';
import { useFieldArray } from 'react-hook-form';

import { Button } from '@mui/material';
import { translate } from '@utils/locale';
import { RhfTextField } from '@components';
import { PlusIcon, TrashIcon } from '@assets/icons';
import { LARGE_FIELD_MAX_LENGTH } from '@constants/common';

import { FaqQuestionsProps } from '../types';

const FaqQuestions: FC<FaqQuestionsProps> = ({
  control,
  errors,
  clearErrors,
  index
}) => {
  const {
    fields: faqQuestionsFields,
    append: appendFaqQuestions,
    remove: removeFaqQuestions
  } = useFieldArray({
    control,
    name: `faq.${index}.questions`
  });

  const handleAddNewMetric = () => {
    appendFaqQuestions({
      id: '',
      question: '',
      answer: ''
    });
  };

  const handleRemoveFaqQuestion = (idx: number) => {
    removeFaqQuestions(idx);
    handleAddNewMetric();
  };

  return (
    <div className="flex flex-col w-full ">
      <div className="grid grid-cols-2 grid-flow-row gap-6">
        {faqQuestionsFields.map((field, idx) => (
          // eslint-disable-next-line tailwindcss/migration-from-tailwind-2
          <div
            key={field.id}
            className="flex flex-col gap-1 p-4 bg-slate-100 bg-opacity-[0.5] rounded border border-lightGrey border-opacity-[0.5]">
            <div className="flex flex-row justify-between text-base font-normal text-black/60">
              <div>
                {translate(
                  'preIpoDeals.createPreIPODeals.dataRoom.faq.questionCount',
                  {
                    count: idx + 1
                  }
                )}
              </div>
              <TrashIcon
                className="w-6 h-6 cursor-pointer trash-icon"
                onClick={() => handleRemoveFaqQuestion(idx)}
              />
            </div>
            <div className="flex flex-col w-full">
              <RhfTextField
                name={`faq.${index}.questions.${idx}.question`}
                label={translate(
                  'preIpoDeals.createPreIPODeals.dataRoom.faq.question'
                )}
                placeholder={translate(
                  'preIpoDeals.createPreIPODeals.dataRoom.faq.questionPlaceholder'
                )}
                control={control}
                errors={{
                  [`faq.${index}.questions.${idx}.question`]:
                    errors.faq?.[index]?.questions?.[idx]?.question
                }}
                multiline={true}
                rows={3}
                wrapperClass="pt-3"
                onClickTextField={() => {
                  clearErrors(`faq.${index}.questions.${idx}.question`);
                }}
                maxLength={LARGE_FIELD_MAX_LENGTH}
                description={translate(
                  'preIpoDeals.createPreIPODeals.dataRoom.faq.questionDescription'
                )}
                showCharacterLimit
              />
              <RhfTextField
                name={`faq.${index}.questions.${idx}.answer`}
                label={translate(
                  'preIpoDeals.createPreIPODeals.dataRoom.faq.answer'
                )}
                placeholder={translate(
                  'preIpoDeals.createPreIPODeals.dataRoom.faq.answerPlaceHolder'
                )}
                control={control}
                errors={{
                  [`faq.${index}.questions.${idx}.answer`]:
                    errors.faq?.[index]?.questions?.[idx]?.answer
                }}
                multiline={true}
                rows={3}
                wrapperClass="pt-3"
                onClickTextField={() => {
                  clearErrors(`faq.${index}.questions.${idx}.answer`);
                }}
                maxLength={LARGE_FIELD_MAX_LENGTH}
                description={translate(
                  'preIpoDeals.createPreIPODeals.dataRoom.faq.answerDescription'
                )}
                showCharacterLimit
              />
            </div>
          </div>
        ))}
      </div>
      <div>
        <div className="group inline-block">
          <Button
            variant="outlined"
            color="info"
            size="small"
            style={{
              marginTop: 16
            }}
            className="group-hover:text-white group-hover:bg-brightBlue"
            startIcon={<PlusIcon />}
            onClick={handleAddNewMetric}>
            {translate(
              'preIpoDeals.createPreIPODeals.dataRoom.faq.addQuestion'
            )}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default FaqQuestions;
